import React, { useEffect, useState } from "react";
import "./ProfilePage.scss";
import avatarImage from "../../assets/images/avatar.webp";
import backgroundimage from "../../assets/images/profile-bkg.jpeg";
import EditProfileModal from "./EditProfileModal";
import NotificationPanel from "../Notification/NotificationPanel";
import PageLoader from "../../pages/PageLoader/PageLoader";
import UserService from "../../services/UserService";
import { IUser } from "../../interfaces/user.interface";
import { useAuth } from "../../contexts/AuthContext";
import { ReactComponent as EditIcon } from "../../assets/icons/edit-icon.svg";
import { ReactComponent as JobsIcon } from "../../assets/icons/jobs-icon.svg";
import { ReactComponent as DriversIcon } from "../../assets/icons/drivers-icon.svg";
import { ReactComponent as TruckIcon } from "../../assets/icons/truck.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/user-icon.svg";
import { ReactComponent as LocationIcon } from "../../assets/icons/location-icon.svg";
import { ReactComponent as TickIcon } from "../../assets/icons/tick.svg";

const ProfilePage: React.FC = () => {
  const { refreshProfile } = useAuth();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<IUser | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const profileData = await UserService.getMyProfile();
        setUserData(profileData);
        console.log("Profile data:", profileData);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Failed to load profile data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleProfileUpdated = async (updatedUser: IUser) => {
    setUserData(updatedUser);
    // Also refresh the global auth context to update ProfileButton
    try {
      await refreshProfile();
    } catch (error) {
      console.error("Error refreshing auth context:", error);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="profile-page p-[20px]">
        <div className="error-message text-red-500 text-center p-4">
          {error}
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-page p-[20px]">
        <div className="error-message text-red-500 text-center p-4">
          No profile data available.
        </div>
      </div>
    );
  }

  // Helper function to format role name for display
  const formatRoleName = (roleName: string) => {
    // Special case for ROLE_CLIENT_ADMIN
    if (roleName === "ROLE_CLIENT_ADMIN") {
      return "Enterprise Admin";
    }

    if (roleName === "ROLE_FEDERATION_ADMIN") {
      return "Federation Admin";
    }

    if (roleName === "ROLE_TRANSPORTER_ADMIN") {
      return "Transporter Admin";
    }

    return roleName
      .replace("ROLE_", "")
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Helper function to format privilege names for display
  const formatPrivilegeName = (privilegeName: string) => {
    return privilegeName
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Helper function to format organization type based on role
  const formatOrgType = (roleName: string) => {
    if (roleName === "ROLE_CLIENT_ADMIN") {
      return "Enterprise";
    }
    if (roleName === "ROLE_FEDERATION_ADMIN") {
      return "Federation";
    }
    if (roleName === "ROLE_TRANSPORTER_ADMIN") {
      return "Transporter";
    }
    // Add more role-based organization types here if needed
    return roleName; // Default fallback
  };

  // Helper function to categorize privileges
  const categorizePrivileges = (privileges: any[]) => {
    const categories = {
      jobManagement: {
        title: "Job Management",
        icon: JobsIcon,
        privileges: privileges.filter(
          (p) => p.name.includes("JOB") && !p.name.includes("TRACK")
        ),
      },
      tracking: {
        title: "Tracking & Monitoring",
        icon: LocationIcon,
        privileges: privileges.filter((p) => p.name.includes("TRACK")),
      },
      transporterManagement: {
        title: "Transporter Management",
        icon: TruckIcon,
        privileges: privileges.filter((p) => p.name.includes("TRANSPORTER")),
      },
      vehicleManagement: {
        title: "Vehicle Management",
        icon: TruckIcon,
        privileges: privileges.filter(
          (p) => p.name.includes("TRUCK") || p.name.includes("TRAILER")
        ),
      },
      driverManagement: {
        title: "Driver Management",
        icon: DriversIcon,
        privileges: privileges.filter((p) => p.name.includes("DRIVER")),
      },
      userManagement: {
        title: "User Management",
        icon: UserIcon,
        privileges: privileges.filter(
          (p) => p.name.includes("USER") || p.name.includes("CREATE_USER")
        ),
      },
    };

    // Filter out empty categories and sort alphabetically by title
    const filteredCategories = Object.fromEntries(
      Object.entries(categories).filter(
        ([_, category]) => category.privileges.length > 0
      )
    );

    return filteredCategories;

    // Sort categories alphabetically by title
    // return Object.fromEntries(
    //   Object.entries(filteredCategories).sort(([, a], [, b]) =>
    //     a.title.localeCompare(b.title)
    //   )
    // );
  };

  const privilegeCategories = categorizePrivileges(userData.role.privileges);

  return (
    <div className="profile-page p-[20px]">
      {/* <NotificationPanel /> */}
      <div className="profile-card">
        <div className="card-top">
          <img
            src={backgroundimage}
            alt="Background"
            className="background-image"
          />
          <div className="avatar">
            <img src={avatarImage} alt="Profile" className="avatar-image" />
          </div>
        </div>

        <div className="containerbox">
          {/* Basic Information Section */}
          <div className="container basic-info">
            <div className="header-row">
              <h2>Basic Information</h2>
              <button
                className="edit-button"
                onClick={() => setIsProfileModalOpen(true)}
              >
                <EditIcon />
                Edit Profile
              </button>
            </div>
            <div className="flex-row">
              <div className="flex-col half-width">
                <div className="label">Full Name:</div>
                <div className="value">{userData.userFullName}</div>
              </div>
              <div className="flex-col half-width">
                <div className="label">Email:</div>
                <div className="value">{userData.email}</div>
              </div>
            </div>
            <div className="flex-row">
              <div className="flex-col half-width">
                <div className="label">Phone Number:</div>
                <div className="value">+91 {userData.phoneNumber}</div>
              </div>
              <div className="flex-col half-width">
                <div className="label">User ID:</div>
                <div className="value">{userData.id}</div>
              </div>
            </div>
          </div>

          {/* Role & Organization Section */}
          <div className="container role-org-info">
            <div className="header-row">
              <h2>Role & Organization</h2>
            </div>
            <div className="flex-row">
              <div className="flex-col half-width">
                <div className="label">Role:</div>
                {/* <div className="value role-badge">
                  {formatRoleName(userData.role.name)}
                </div> */}
                <div className="value">
                  {formatRoleName(userData.role.name)}
                </div>
              </div>
              <div className="flex-col half-width">
                <div className="label">Organization:</div>
                <div className="value">{userData.org.name}</div>
              </div>
            </div>
            <div className="flex-row">
              <div className="flex-col half-width">
                <div className="label">Organization Type:</div>
                <div className="value">{formatOrgType(userData.role.name)}</div>
              </div>
              <div className="flex-col half-width">
                <div className="label">Organization ID:</div>
                <div className="value">{userData.org.id}</div>
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          {(userData.address || userData.city || userData.state) && (
            <div className="container address-info">
              <div className="header-row">
                <h2>Address Information</h2>
                <button
                  className="edit-button"
                  onClick={() => setIsProfileModalOpen(true)}
                >
                  <EditIcon />
                  Edit Address
                </button>
              </div>
              {userData.address && (
                <div className="flex-row">
                  <div className="flex-col full-width">
                    <div className="label">Address:</div>
                    <div className="value">{userData.address}</div>
                  </div>
                </div>
              )}
              {(userData.city || userData.state) && (
                <div className="flex-row">
                  <div className="flex-col half-width">
                    <div className="label">City:</div>
                    <div className="value">
                      {userData.city || "Not specified"}
                    </div>
                  </div>
                  <div className="flex-col half-width">
                    <div className="label">State:</div>
                    <div className="value">
                      {userData.state || "Not specified"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Account Status Section */}
          <div className="container account-status">
            <div className="header-row">
              <h2>Account Status</h2>
            </div>
            <div className="flex-row">
              <div className="flex-col half-width">
                <div className="label">Account Status:</div>
                <div
                  className={`value status-badge ${
                    userData.disabled ? "disabled" : "active"
                  }`}
                >
                  {userData.disabled ? "Disabled" : "Active"}
                </div>
              </div>
              <div className="flex-col half-width">
                <div className="label">Email Verified:</div>
                <div className="value">
                  {userData.verifiedEmailDate ? "Yes" : "No"}
                </div>
              </div>
            </div>
            <div className="flex-row">
              <div className="flex-col half-width">
                <div className="label">Phone Verified:</div>
                <div className="value">
                  {userData.verifiedOTPDate ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex-col half-width">
                <div className="label">Password:</div>
                <div className="value">{"*".repeat(8)}</div>
              </div>
            </div>
          </div>

          {/* Privileges Section */}
          <div className="container privileges-info">
            <div className="header-row">
              <h2>Permissions & Privileges</h2>
              {/* <div className="privilege-summary">
                <span className="total-privileges">
                  {userData.role.privileges.length} Total Permissions
                </span>
              </div> */}
            </div>

            <div className="privileges-categories">
              {Object.entries(privilegeCategories).map(([key, category]) => (
                <div key={key} className="privilege-category">
                  <div className="category-header">
                    <category.icon className="category-icon" />
                    <h3 className="category-title">{category.title}</h3>
                    {/* <span className="category-count">
                      {category.privileges.length}
                    </span> */}
                  </div>
                  <div className="category-privileges">
                    {category.privileges.map((privilege, index) => (
                      <div key={index} className="privilege-item">
                        <TickIcon className="privilege-icon" />
                        <span className="privilege-name">
                          {formatPrivilegeName(privilege.name)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        userData={userData}
        onProfileUpdated={handleProfileUpdated}
      />
    </div>
  );
};

export default ProfilePage;
